import { create } from "zustand";

/**
 * Sidebar profile — Simple / Full (bots A4, 2026-09-03).
 *
 * "Simple" is the short sidebar a new install starts with: Chat, Agents,
 * Routines, Inbox, Settings — the surfaces a person needs to hand work to an
 * agent and see what needs them. "Full" is the whole workspace, shown as the
 * More pill since 2026-10-03 (every page in folding groups, System included;
 * the Files pill swaps the list for the files panel). The pills at the top of
 * the sidebar switch between them; Settings → Appearance has the same switch.
 * The stored value stays "full" so no device's choice is lost to the rename.
 *
 * Per-device preference like the theme and the UI scale: localStorage, never
 * the server. A NEW device (nothing stored, and no sidebar-mode key from
 * earlier use of the full sidebar) starts Simple; a device that has used the
 * full sidebar keeps it — nobody's workspace shrinks on upgrade.
 */
export type SidebarProfile = "simple" | "full";

export const SIDEBAR_PROFILES: readonly SidebarProfile[] = ["simple", "full"] as const;

const STORAGE_KEY = "agnt-sidebar-profile";
/** The full sidebar's own mode key — its presence means "this device already
 *  used the full workspace" (Sidebar.tsx writes it on every mode change). */
const LEGACY_MODE_KEY = "agnt-sidebar-mode";

function isProfile(value: unknown): value is SidebarProfile {
	return value === "simple" || value === "full";
}

export function getInitialSidebarProfile(): SidebarProfile {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (isProfile(stored)) return stored;
		// Nothing chosen yet: a device that has used the full sidebar keeps it.
		return localStorage.getItem(LEGACY_MODE_KEY) ? "full" : "simple";
	} catch {
		return "full"; // storage unavailable — never hide a returning user's workspace
	}
}

interface SidebarProfileState {
	profile: SidebarProfile;
	setProfile: (profile: SidebarProfile) => void;
}

export const useSidebarProfileStore = create<SidebarProfileState>((set) => ({
	profile: getInitialSidebarProfile(),
	setProfile: (profile) => {
		try {
			localStorage.setItem(STORAGE_KEY, profile);
		} catch {
			/* storage unavailable — the choice still applies this session */
		}
		set({ profile });
	},
}));
